/**
 * A vetted ISO-4217 currency list for the setup-only currency selector
 * (DNKADM-0050). The provisioning constraint (handoff §5.1) is that the tenant
 * currency is a 0- or 2-decimal ISO currency (>2-decimal currencies are rejected),
 * so this list is intentionally limited to common 0-/2-decimal codes. The server
 * remains the authority — an off-list code is still rejected server-side; this is
 * the curated picker set, not an exhaustive registry.
 */

export interface CurrencyOption {
  /** ISO-4217 alphabetic code (CHAR(3)). */
  code: string;
  /** Human label for the picker. */
  label: string;
}

/** Common 0-/2-decimal ISO-4217 currencies offered in the setup picker. */
export const CURRENCY_OPTIONS: readonly CurrencyOption[] = [
  { code: 'USD', label: 'US Dollar' },
  { code: 'EUR', label: 'Euro' },
  { code: 'GBP', label: 'British Pound' },
  { code: 'CAD', label: 'Canadian Dollar' },
  { code: 'AUD', label: 'Australian Dollar' },
  { code: 'NZD', label: 'New Zealand Dollar' },
  { code: 'CHF', label: 'Swiss Franc' },
  { code: 'SEK', label: 'Swedish Krona' },
  { code: 'NOK', label: 'Norwegian Krone' },
  { code: 'DKK', label: 'Danish Krone' },
  { code: 'JPY', label: 'Japanese Yen' }, // 0-decimal
  { code: 'KRW', label: 'South Korean Won' }, // 0-decimal
  { code: 'SGD', label: 'Singapore Dollar' },
  { code: 'HKD', label: 'Hong Kong Dollar' },
  { code: 'MXN', label: 'Mexican Peso' },
  { code: 'BRL', label: 'Brazilian Real' },
  { code: 'ZAR', label: 'South African Rand' },
  { code: 'INR', label: 'Indian Rupee' },
  { code: 'AED', label: 'UAE Dirham' },
];

/**
 * The picker option for a code, or a synthesized "code only" option when a stored
 * currency (server-set) is not in the curated list — so a locked/known currency
 * always renders even if it is off-list.
 */
export function currencyOptionFor(code: string): CurrencyOption {
  return CURRENCY_OPTIONS.find((c) => c.code === code) ?? { code, label: code };
}
